/* search-brands.js: <sf-search-brands>, the "Popular brands" chips (snippets/search-brands.liquid).
   The snippet prints the element hidden, with its heading and an empty list. This script fills the list with the
   brands that sections/search-brands.liquid proves to have stock, fetched through the Section Rendering API from
   data-url (/collections/all?filter.v.availability=1&section_id=search-brands).

   · When: inside the search sheet, on the sheet's drawer:open (the request starts after the opening tap has
     already focused the input, and never delays it); anywhere else (the no-results page, the copy under a
     predictive "no results" message), as soon as the element is connected.
   · Once: one request per URL per session. The answer is kept in sessionStorage for 10 minutes, and concurrent
     instances share the request in flight. Every storage read and write is wrapped in try/catch; blocked storage
     just means a fresh request on the next open.
   · Honest: chips are shown only for data-state="ok" with at least one brand. "unavailable" (the availability
     filter is not switched on in Search & Discovery), no brands, an error or an unexpected response all leave
     the group hidden, heading included. It never falls back to a guess.
   After every render it dispatches a bubbling `search-brands:change` so the search panel can recheck whether it
   has anything to show. Works without predictive-search.js. */
'use strict';
(function () {
  if (customElements.get('sf-search-brands')) return;
  const TTL = 10 * 60 * 1000;
  const KEY = 'sf:search-brands:';
  const MAX = 8;
  const pending = {};
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  /* the section only ever links inside the shop: a root-relative path, never a protocol or another host */
  const safeHref = (u) => typeof u === 'string' && /^\/(?!\/)/.test(u) && !/[\s<>"]/.test(u);

  function readCache(url) {
    try {
      const d = JSON.parse(window.sessionStorage.getItem(KEY + url) || 'null');
      if (!d || typeof d.t !== 'number' || !Array.isArray(d.b)) return null;
      const age = Date.now() - d.t;
      return age >= 0 && age < TTL ? d : null;
    } catch (e) { return null; }
  }
  function writeCache(url, d) {
    try { window.sessionStorage.setItem(KEY + url, JSON.stringify(d)); } catch (e) { /* storage blocked or full */ }
  }

  /* section HTML → { t, s: 'ok' | 'unavailable', b: [[label, href], …] }; null when the response is not ours */
  function parse(html) {
    const doc = new DOMParser().parseFromString(String(html), 'text/html');
    const root = doc.querySelector('[data-search-brands-data]');
    if (!root) return null;
    const ok = root.getAttribute('data-state') === 'ok';
    const b = [];
    if (ok) {
      root.querySelectorAll('a[href]').forEach((a) => {
        const label = a.textContent.replace(/\s+/g, ' ').trim();
        const href = a.getAttribute('href');
        if (label && safeHref(href) && b.length < MAX) b.push([label, href]);
      });
    }
    return { t: Date.now(), s: ok ? 'ok' : 'unavailable', b };
  }

  /* the page's own copy of each answer, so blocked storage still means one request per page, not one per open;
     a failed request is remembered too, and retried only after RETRY_MS */
  const memo = {};
  const RETRY_MS = 60 * 1000;

  function load(url) {
    const m = memo[url];
    if (m && m.d && Date.now() - m.d.t < TTL) return Promise.resolve(m.d);
    if (m && m.failed && Date.now() - m.failed < RETRY_MS) return Promise.reject(new Error('search-brands: recent failure'));
    const cached = readCache(url);
    if (cached) { memo[url] = { d: cached }; return Promise.resolve(cached); }
    if (pending[url]) return pending[url];
    const get = window.theme && typeof window.theme.fetchText === 'function'
      ? window.theme.fetchText(url, { headers: { Accept: 'text/html' } })
      : fetch(url, { headers: { Accept: 'text/html' } }).then((r) => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); });
    const p = get.then((html) => {
      const d = parse(html);
      if (!d) throw new Error('unexpected search-brands response');
      writeCache(url, d);
      memo[url] = { d };
      return d;
    });
    pending[url] = p;
    const done = () => { delete pending[url]; };
    p.then(done, () => { memo[url] = { failed: Date.now() }; done(); });
    return p;
  }

  class SFSearchBrands extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-search-brands-list]');
      this.url = this.dataset.url;
      if (!this.list || !this.url) return;
      this.limit = Math.min(MAX, parseInt(this.dataset.limit, 10) || MAX);
      this._ctrl = new AbortController();
      const cached = readCache(this.url);
      /* a fresh answer from earlier in the session is drawn at once, so a reopened sheet does not shift */
      if (cached) this.render(cached);
      const drawer = this.closest('sf-drawer');
      if (drawer) {
        drawer.addEventListener('drawer:open', (e) => { if (e.target === drawer) this.refresh(); }, { signal: this._ctrl.signal });
        if (!drawer.hidden) this.refresh();
      } else {
        this.refresh();
      }
    }

    disconnectedCallback() {
      if (this._ctrl) this._ctrl.abort();
    }

    refresh() {
      load(this.url).then((d) => { if (this.isConnected) this.render(d); }, () => { if (this.isConnected && !this._key) this.render(null); });
    }

    render(d) {
      const items = d && d.s === 'ok' ? d.b.slice(0, this.limit) : [];
      const key = (d ? d.s : 'error') + JSON.stringify(items);
      if (key === this._key) return;
      this._key = key;
      this.dataset.state = d ? d.s : 'error';
      if (!items.length) {
        this.list.replaceChildren();
        this.hidden = true;
      } else {
        this.list.innerHTML = items.map(([label, href]) =>
          '<li><a class="brand-chip" href="' + esc(href) + '" data-term="' + esc(label) + '">' + esc(label) + '</a></li>').join('');
        this.hidden = false;
      }
      this.dispatchEvent(new CustomEvent('search-brands:change', { bubbles: true, detail: { count: items.length, state: this.dataset.state } }));
    }
  }
  customElements.define('sf-search-brands', SFSearchBrands);
})();
